import { NetworkStatus } from '@apollo/client';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { MilestoneType } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { FieldArray, FieldProps } from 'formik';
import { filter, findIndex, get, includes, orderBy, some } from 'lodash';
import React, { Fragment } from 'react';
import { ErrorRetry } from '~/components/atoms/error-retry';
import { ListSelectionStyled, SubListSelectionStyled } from '~/components/ions/drawer';
import { useAppUser } from '~/components/molecules/appuser/appuser-context';
import { useAvailableMilestoneTypesQuery } from '~/hooks/use-available-milestone-types';

interface MilestonesProps extends FieldProps {
  excludeMilestoneTypes?: string[];
}

export const Milestones: React.FC<MilestonesProps> = ({ form, excludeMilestoneTypes = [] }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useAvailableMilestoneTypesQuery(user?.project?.id);

  const milestoneList: MilestoneType[] = orderBy(
    filter(
      get(data, 'projectConfigById.availableMilestoneTypes', []),
      ({ id }) => !includes(excludeMilestoneTypes, id.toUpperCase())
    ),
    [(item) => item?.commodityTypes?.[0]?.name?.toLowerCase() || '', (item) => item?.name?.toLowerCase() || ''],
    ['asc', 'asc']
  );

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans> There was a problem loading Milestones List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.filterByMilestoneTypes?.length !== milestoneList.length) {
                form.setFieldValue('filterByMilestoneTypes', milestoneList);
              } else {
                form.setFieldValue('filterByMilestoneTypes', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.filterByMilestoneTypes?.length === milestoneList.length}
                  readOnly
                  indeterminate={
                    form.values.filterByMilestoneTypes?.length > 0 &&
                    form.values.filterByMilestoneTypes?.length < milestoneList.length
                  }
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans> Milestone</Trans>
              </Text>
            </Box>
            {form.values.filterByMilestoneTypes && form.values.filterByMilestoneTypes.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.filterByMilestoneTypes.length} {t`Selected`}
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="filterByMilestoneTypes">
            {(arrayHelpers) => {
              return (
                <>
                  {milestoneList &&
                    milestoneList.map((milestoneType: MilestoneType) => {
                      const displayText = milestoneType.commodityTypes?.[0]?.name
                        ? `${milestoneType.name} (${milestoneType.commodityTypes?.[0]?.name})`
                        : milestoneType.name;
                      return (
                        <ListSelectionStyled
                          key={milestoneType.id}
                          data-testid={`planning-wp-filter-by-milestone-types-${milestoneType.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.filterByMilestoneTypes, (item: MilestoneType) => {
                              return item.id === milestoneType.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(milestoneType);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.filterByMilestoneTypes, (item: MilestoneType) => {
                                return item.id === milestoneType.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Text size="400" color="bechtel.grey.1000">
                            {displayText}
                          </Text>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
